import { useMemo, useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";
import { printInvoice, money } from "./FacilityJobsPage";

/**
 * Facility-side invoices list. Sourced from publicInvoicesRaw (the
 * cross-org mirror written by mirrorInvoice when a contractor finalizes
 * a bill on a job at one of this facility's buildings). Each row shows
 * the total + a View / Print Invoice button that reuses the existing
 * printInvoice helper for an HTML print window the user can save as PDF.
 */
export default function FacilityInvoicesPage({
  publicInvoicesRaw = [],
  customers = [],
  actions,
  onBack,
}) {
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [ackFilter, setAckFilter] = useState(""); // "" | "pending" | "acknowledged"
  const [ackingId, setAckingId] = useState(null);

  // Helpers for building / customer display lookup. Mirrors what
  // FacilityJobsPage does so the invoice row reads as "Customer · Building".
  const buildings = useMemo(
    () =>
      customers.flatMap((c) =>
        (c.locations || []).map((b) => ({ ...b, customer: c }))
      ),
    [customers]
  );
  const buildingFor = (id) => buildings.find((b) => b.id === id);
  const buildingName = (id) => buildingFor(id)?.name || "—";
  const customerNameFor = (locationId) =>
    buildingFor(locationId)?.customer?.name || "—";

  // Apply filters. Drop sourceDeleted mirrors (the contractor un-finalized
  // the bill or deleted the job) — they no longer represent a real invoice.
  const invoices = publicInvoicesRaw
    .filter((i) => !i.sourceDeleted)
    .filter((i) => !buildingFilter || i.locationId === buildingFilter)
    .filter((i) => {
      if (ackFilter === "pending") return !i.acknowledged;
      if (ackFilter === "acknowledged") return !!i.acknowledged;
      return true;
    })
    .filter((i) => {
      const term = search.trim().toLowerCase();
      if (!term) return true;
      const hay = [
        i.invoiceNumber,
        i.jobNumber,
        i.jobId,
        i.title,
        buildingName(i.locationId),
        customerNameFor(i.locationId),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    })
    // Most recently finalized first (finalizedAt set by contractor's bill
    // finalize action; falls back to createdAt for older mirrors).
    .sort((a, b) => {
      const ak = a.finalizedAt || a.createdAt || "";
      const bk = b.finalizedAt || b.createdAt || "";
      return bk.localeCompare(ak);
    });

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Invoices</h2>

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
        <input
          type="text"
          placeholder="Search by job # or text..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, minWidth: "200px" }}
        />
        <select
          value={buildingFilter}
          onChange={(e) => setBuildingFilter(e.target.value)}
        >
          <option value="">All buildings</option>
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select
          value={ackFilter}
          onChange={(e) => setAckFilter(e.target.value)}
          title="Filter by acknowledge status"
        >
          <option value="">All ack status</option>
          <option value="pending">Awaiting acknowledgement</option>
          <option value="acknowledged">Acknowledged</option>
        </select>
      </div>

      {invoices.length === 0 ? (
        <div
          style={{
            padding: "16px",
            color: "#888",
            border: "1px dashed #ddd",
            borderRadius: "6px",
          }}
        >
          No invoices yet. Once your contractor finalizes a bill on a job at
          one of your buildings, it shows up here.
        </div>
      ) : (
        <div style={styles.card}>
          {invoices.map((inv) => (
            <div
              key={inv.id || inv.jobId}
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: "200px" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textDecoration: inv.voided ? "line-through" : "none",
                    color: inv.voided ? "#999" : "inherit",
                  }}
                >
                  {inv.invoiceNumber
                    ? `Invoice ${inv.invoiceNumber}${
                        inv.jobNumber ? ` · Job #${inv.jobNumber}` : ""
                      }`
                    : inv.jobNumber
                    ? `Job #${inv.jobNumber}`
                    : "Invoice"}
                  {inv.title ? ` — ${inv.title}` : ""}
                  {inv.finalizedAt && (
                    <span
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        fontWeight: 400,
                        marginLeft: "6px",
                      }}
                    >
                      ({new Date(inv.finalizedAt).toLocaleDateString()})
                    </span>
                  )}
                </div>
                {inv.voided && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#b91c1c",
                      fontWeight: 700,
                      marginTop: "2px",
                    }}
                  >
                    VOIDED
                    {inv.voidedAt
                      ? ` ${new Date(inv.voidedAt).toLocaleDateString()}`
                      : ""}
                    {inv.voidReason
                      ? ` — ${inv.voidReason.replace(/_/g, " ")}`
                      : ""}
                  </div>
                )}
                <div style={{ fontSize: "13px", color: "#666" }}>
                  {customerNameFor(inv.locationId)} · {buildingName(inv.locationId)}
                </div>
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: inv.voided ? "#999" : "#1a7f37",
                  textDecoration: inv.voided ? "line-through" : "none",
                }}
              >
                {money(inv.grandTotal)}
              </div>
              {inv.voided ? (
                // Voided invoices can't be acknowledged — no payment owed.
                // Suppress the Acknowledge button entirely.
                null
              ) : inv.acknowledged ? (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 8px",
                    background: "#d4edda",
                    color: "#1a5f1f",
                    borderRadius: "3px",
                    fontWeight: 600,
                  }}
                  title={
                    inv.acknowledgedAt
                      ? `Acknowledged ${new Date(
                          inv.acknowledgedAt
                        ).toLocaleString()}${
                          inv.acknowledgedBy
                            ? ` by ${inv.acknowledgedBy}`
                            : ""
                        }`
                      : "Acknowledged"
                  }
                >
                  Acknowledged
                </span>
              ) : (
                actions?.acknowledgeInvoice && (
                  <button
                    style={{
                      ...styles.button,
                      background: "#fff8e0",
                      color: "#6a4900",
                      border: "1px solid #f0d27a",
                    }}
                    onClick={() => {
                      setAckingId(inv.id);
                      actions
                        .acknowledgeInvoice(inv.id)
                        .finally(() => setAckingId(null));
                    }}
                    disabled={ackingId === inv.id}
                    title="Mark this invoice as received and reviewed by your office."
                  >
                    {ackingId === inv.id ? "Saving…" : "Acknowledge"}
                  </button>
                )
              )}
              <button
                style={styles.button}
                onClick={() => {
                  const bld = buildingFor(inv.locationId);
                  printInvoice(inv, bld, bld?.customer);
                }}
              >
                View / Print
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
